import { Box } from "@mui/material";
import { v2, nums } from "../ui/v2Tokens";
import { bandTone } from "../ui/bandTone";
import { dayBand, dayDeviationPct } from "../builder/targetsMath";
import { HE_DAY_LETTERS, HE_MONTHS, monthCells, parts } from "./hebrewDates";
import { metricFigure, metricValue, type LogMetric } from "./monthMetrics";
import { useNutritionDisplayMode } from "../../../../contexts/NutritionDisplayContext";
import type { DaySummaryV2 } from "../../../../services/FoodLogService";

type Props = {
  /** YYYY-MM-01 */
  monthStart: string;
  days: DaySummaryV2[];
  metric: LogMetric;
  /** YYYY-MM-DD — the open day; may be outside this month, in which case no cell is ringed */
  selected: string;
  /** YYYY-MM-DD */
  today: string;
  onSelect: (date: string) => void;
};

/**
 * The month as a grid, one cell per day, coloured by how far that day landed from its target.
 *
 * ── the rule that matters most here ──
 *
 * A day the trainee did NOT report still has food attached: the day is materialised from the assigned
 * menu, so its totals look exactly like intake. Such a cell shows "לא דווח" and nothing else — no
 * number, no percentage, no tint. Colouring it would credit a trainee for a plan nobody says they
 * followed, and a trainer looking at a calendar has no way to tell the difference.
 *
 * The tint and the percentage both come from `targetsMath` — the cell classifies nothing itself, so
 * it cannot drift from the tiles below it or from the summary line above.
 */
const MonthGrid = ({ monthStart, days, metric, selected, today, onSelect }: Props) => {
  const byDate = new Map(days.map((day) => [day.date, day]));
  const mode = useNutritionDisplayMode();

  return (
    <Box>
      <Box
        aria-hidden
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gap: { xs: "3px", sm: "6px" },
          marginBottom: "6px",
        }}
      >
        {HE_DAY_LETTERS.map((letter) => (
          <Box
            key={letter}
            sx={{ textAlign: "center", fontSize: "11px", fontWeight: 700, color: v2.ink4 }}
          >
            {letter}
          </Box>
        ))}
      </Box>

      <Box
        role="grid"
        aria-label="ימי החודש"
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gap: { xs: "3px", sm: "6px" },
        }}
      >
        {monthCells(monthStart).map((date, index) => {
          if (!date) return <Box key={`blank-${index}`} />;

          const { day: dayNumber, month } = parts(date);
          const monthName = HE_MONTHS[month - 1];
          const isSelected = date === selected;
          const isToday = date === today;
          const isFuture = date > today;
          const summary = byDate.get(date);
          const reported = Boolean(summary?.reported);

          const value = metricValue(summary?.totals, metric);
          const target = metricValue(summary?.targets, metric);
          const band = reported ? dayBand(value, target) : "none";
          const deviation = reported ? dayDeviationPct(value, target) : null;
          const tone = bandTone[band];
          // the number and its unit from one call — in points mode both change together
          const figure = metricFigure(value, metric, mode);

          // Everything the colours say, said in words — the tint is not available to a screen reader,
          // and "לא דווח" is the single most important thing this grid communicates.
          const label = isFuture
            ? `${dayNumber} ב${monthName} — טרם הגיע`
            : !reported
              ? `${dayNumber} ב${monthName} — לא דווח`
              : deviation === null
                ? `${dayNumber} ב${monthName} — ${figure.text} ${figure.unit}, אין יעד ליום זה`
                : `${dayNumber} ב${monthName} — ${figure.text} ${figure.unit}, ${
                    deviation > 0 ? `${deviation}% מעל היעד` : deviation < 0 ? `${-deviation}% מתחת ליעד` : "על היעד"
                  }`;

          return (
            <Box
              key={date}
              component="button"
              type="button"
              role="gridcell"
              data-testid={`month-cell-${date}`}
              aria-label={label}
              aria-selected={isSelected}
              aria-current={isToday ? "date" : undefined}
              disabled={isFuture}
              onClick={() => onSelect(date)}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: "2px",
                minHeight: { xs: 58, sm: 76 },
                padding: { xs: "5px 6px 6px", sm: "6px 8px 8px" },
                textAlign: "start",
                fontFamily: "inherit",
                borderRadius: v2.rSm,
                cursor: isFuture ? "default" : "pointer",
                transition: "box-shadow .12s, transform .12s",
                background: isFuture ? v2.canvas : reported ? tone.fill : v2.surface,
                // a dashed edge is the unreported day's whole signal — it must not look like a
                // quiet version of a coloured one
                border: isFuture
                  ? `1px solid ${v2.lineSoft}`
                  : reported
                    ? `1px solid ${v2.line}`
                    : `1px dashed ${v2.line}`,
                // the ring rides on top of the band rather than replacing it, so opening a day never
                // hides how that day went
                boxShadow: isSelected ? `0 0 0 2px ${v2.brand}` : "none",
                "&:hover": isFuture ? {} : { transform: "translateY(-1px)", boxShadow: isSelected ? `0 0 0 2px ${v2.brand}` : v2.shadow },
              }}
            >
              <Box
                sx={{
                  fontSize: "11px",
                  fontWeight: isToday ? 800 : 600,
                  lineHeight: 1,
                  color: isToday ? v2.brandText : v2.ink4,
                  ...nums,
                }}
              >
                {dayNumber}
              </Box>

              {isFuture ? null : !reported ? (
                <Box sx={{ marginTop: "auto", fontSize: "10.5px", fontWeight: 600, color: v2.ink4 }}>
                  לא דווח
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      marginTop: "auto",
                      display: "flex",
                      alignItems: "baseline",
                      gap: "3px",
                      lineHeight: 1.05,
                      ...nums,
                    }}
                  >
                    <Box sx={{ fontSize: { xs: "13px", sm: "15px" }, fontWeight: 700, color: tone.text, letterSpacing: "-.02em" }}>
                      {figure.text}
                    </Box>
                    <Box sx={{ fontSize: "9px", fontWeight: 500, color: v2.ink4 }}>{figure.unit}</Box>
                  </Box>

                  {deviation !== null && (
                    <Box sx={{ fontSize: "10px", fontWeight: 700, color: tone.text, ...nums }}>
                      {deviation > 0 ? "+" : ""}
                      {deviation}%
                    </Box>
                  )}

                  {target > 0 && (
                    <Box sx={{ height: "3px", borderRadius: "99px", background: "rgba(20,28,36,.07)", overflow: "hidden", marginTop: "3px" }}>
                      <Box
                        sx={{
                          height: "100%",
                          borderRadius: "99px",
                          width: `${Math.min(100, Math.max(0, (value / target) * 100))}%`,
                          background: tone.text,
                          opacity: 0.55,
                        }}
                      />
                    </Box>
                  )}
                </>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default MonthGrid;
